import { BdcCompletenessRow, BdcImportPreviewData } from "../../../types/bdc.type";

/** One DC on one order day: its rounds side by side, like the prototype's DC × รอบ matrix. */
export interface CompletenessGroup {
  key: string;
  order_date: string;
  dc_code: string;
  dc_name: string;
  /** The DC's delivering BDC branch; null while the DC is new or unbound. */
  company_id: string | null;
  branch_code: string | null;
  is_d_plus_1: boolean;
  byRound: Map<number, BdcCompletenessRow>;
  /** kind A with no PICKLST (or no round to place it under) — ขาดรายร้าน. */
  missing: BdcCompletenessRow[];
  /** kind X — PO with no per-store lines by design. */
  noStores: BdcCompletenessRow[];
  /** Already stored as a draft — the confirm overwrites them. */
  alreadyImported: number;
  /** Stored past draft (ยืนยัน DO onward) — the confirm leaves them as they are. */
  locked: number;
  pieces: number;
}

export const buildCompleteness = (rows: BdcCompletenessRow[]) => {
  const groups = new Map<string, CompletenessGroup>();
  const rounds = new Set<number>();

  rows.forEach((row) => {
    const key = `${row.order_date}|${row.dc_code}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        order_date: row.order_date,
        dc_code: row.dc_code,
        dc_name: row.dc_name,
        company_id: row.company_id ?? null,
        branch_code: row.branch_code ?? null,
        is_d_plus_1: false,
        byRound: new Map(),
        missing: [],
        noStores: [],
        alreadyImported: 0,
        locked: 0,
        pieces: 0,
      };
      groups.set(key, group);
    }
    group.pieces += row.pieces ?? 0;
    if (row.is_locked) group.locked += 1;
    else if (row.already_imported) group.alreadyImported += 1;
    if (row.kind === "A") group.is_d_plus_1 = group.is_d_plus_1 || row.is_d_plus_1;

    if (row.kind === "X") {
      group.noStores.push(row);
    } else if (!row.has_picklist || row.round === null) {
      group.missing.push(row);
    } else {
      group.byRound.set(row.round, row);
      rounds.add(row.round);
    }
  });

  const list = Array.from(groups.values()).sort(
    (a, b) => a.order_date.localeCompare(b.order_date) || a.dc_code.localeCompare(b.dc_code),
  );
  return { groups: list, rounds: Array.from(rounds).sort((a, b) => a - b) };
};

/**
 * Checks that stop the confirm button; the rest are information only. `errors` already holds
 * one line per unbound DC and per duplicate file name (the server adds them there).
 */
export const blockingCount = (preview: BdcImportPreviewData): number =>
  preview.checks.mismatches.length +
  preview.checks.orphan_picklist.length +
  preview.errors.length;

/** The server's verdict (`is_blocked`) or any blocking check seen here — either stops the confirm. */
export const isConfirmBlocked = (preview: BdcImportPreviewData): boolean =>
  Boolean(preview.is_blocked) || blockingCount(preview) > 0 || preview.completeness.length === 0;

/** "ระบบแยกให้ตามสาขา" — the POs of the upload per BDC branch; company_id null = DC not bound yet. */
export interface BranchSplitRow {
  company_id: string | null;
  branch_code: string | null;
  dc_codes: string[];
  po_count: number;
  store_count: number;
  pieces: number;
}

export const buildBranchSplit = (rows: BdcCompletenessRow[]): BranchSplitRow[] => {
  const byBranch = new Map<string, BranchSplitRow & { dcs: Set<string> }>();
  rows.forEach((row) => {
    const key = row.company_id ?? "";
    let split = byBranch.get(key);
    if (!split) {
      split = {
        company_id: row.company_id ?? null,
        branch_code: row.branch_code ?? null,
        dc_codes: [],
        dcs: new Set(),
        po_count: 0,
        store_count: 0,
        pieces: 0,
      };
      byBranch.set(key, split);
    }
    split.dcs.add(row.dc_code);
    split.po_count += 1;
    split.store_count += row.kind === "X" ? 0 : row.store_count ?? 0;
    split.pieces += row.pieces ?? 0;
  });
  return Array.from(byBranch.values())
    .map(({ dcs, ...split }) => ({ ...split, dc_codes: Array.from(dcs).sort() }))
    .sort((a, b) => {
      // unbound last, then by branch code
      if (a.company_id === null) return 1;
      if (b.company_id === null) return -1;
      return (a.branch_code ?? "").localeCompare(b.branch_code ?? "");
    });
};

/** POs the confirm will overwrite: imported before and still a draft (a locked PO is skipped). */
export const overwriteCount = (rows: BdcCompletenessRow[]): number =>
  rows.filter((row) => row.already_imported && !row.is_locked).length;
